import { initReactI18next } from 'react-i18next';
import { GENERAL_CONSTANTS } from '@components-shared/general.constants';
import { localizationResources } from '@locales/constants';
import { getURLParams, numberFormat } from '@utils/helper';
import { cookieService, Cookies } from '@utils/library/cookie.service';
import { ENUM_LANGUAGE_CODE, ENUM_LOCALE_CODE, LANGUAGE_CODE_TO_LOCALE_CODE } from 'global.enums';
import i18next from 'i18next';

export const isLocaleExists = (locale: ENUM_LOCALE_CODE): boolean => {
  const locales = GENERAL_CONSTANTS.LOCALES;

  return Object.values(locales).some((l) => l.key === locale);
};

export const getPresetLocale = (): ENUM_LOCALE_CODE | null => {
  const params = getURLParams();
  const { lang } = params;

  switch (lang) {
    case ENUM_LANGUAGE_CODE.en:
      return ENUM_LOCALE_CODE.enUS;
    case ENUM_LANGUAGE_CODE.ru:
      return ENUM_LOCALE_CODE.ru;
    case ENUM_LANGUAGE_CODE.zh:
      return ENUM_LOCALE_CODE.zhCN;
    case ENUM_LANGUAGE_CODE.ms:
      return ENUM_LOCALE_CODE.msMY;
    case ENUM_LANGUAGE_CODE.th:
      return ENUM_LOCALE_CODE.thTH;
    case ENUM_LANGUAGE_CODE.id:
      return ENUM_LOCALE_CODE.idID;
    case ENUM_LANGUAGE_CODE.vi:
      return ENUM_LOCALE_CODE.viVN;
    case ENUM_LANGUAGE_CODE.es:
      return ENUM_LOCALE_CODE.esES;
    case ENUM_LANGUAGE_CODE.ar:
      return ENUM_LOCALE_CODE.ar;
    case ENUM_LANGUAGE_CODE.pt:
      return ENUM_LOCALE_CODE.ptPT;

    default:
      return null;
  }
};

export const setLanguage = async (locale: ENUM_LOCALE_CODE = getDefaultLocale()) => {
  const rootCookiePath = '/';

  cookieService.setCookie(Cookies.culture, locale, rootCookiePath);

  await i18next.changeLanguage(locale);
};

export const i18nextInitialize = async (locale: ENUM_LOCALE_CODE = getDefaultLocale()) => {
  try {
    await i18next.use(initReactI18next).init({
      lng: locale,
      resources: localizationResources,
      interpolation: {
        format: (value, format, lng) => numberFormat(value, String(format), lng),
      },
      compatibilityJSON: 'v4',
      returnObjects: true,
    });
  } catch (e) {
    console.error(
      `An error occurred while initializing i18next for locale ${locale}.\nError: ${e.message}. Stack trace: ${e.stack}`,
    );
  }
};

export const getCulture = (): ENUM_LOCALE_CODE => {
  const cookieCurrentCulture = cookieService.getCookie(Cookies.culture) as ENUM_LOCALE_CODE;

  const locales = GENERAL_CONSTANTS.LOCALES;
  const localesCodes = Object.values(locales).map((l) => l.key);

  const codeIndex = localesCodes.findIndex((x) => x === cookieCurrentCulture);

  const currentCulture = codeIndex !== -1 ? localesCodes[codeIndex] : getDefaultLocale();

  if (i18next.isInitialized && i18next.language !== currentCulture) {
    void setLanguage(currentCulture);
  }

  return currentCulture;
};

export const getDefaultLocale = (): ENUM_LOCALE_CODE => {
  const presetLocale = getPresetLocale();

  return presetLocale && isLocaleExists(presetLocale) ? presetLocale : GENERAL_CONSTANTS.DEFAULT_LOCALE;
};

export const getCurrentLanguage = (): ENUM_LOCALE_CODE => {
  const { language } = i18next;

  const languageFromEnum = (Object.values(LANGUAGE_CODE_TO_LOCALE_CODE) as string[]).includes(language);

  if (languageFromEnum) {
    return language as ENUM_LOCALE_CODE;
  }

  return LANGUAGE_CODE_TO_LOCALE_CODE.en;
};
